'use client';

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import {
  countPhrase,
  dotPositions,
  drawnRange,
  pounds,
  priceRangeEmptyText,
  priceRangeFooter,
  stripScale,
  stripX,
  PRICE_RANGE_STATUS_LABEL,
  type PriceRangeBlock,
  type PriceRangeDot,
} from '@/lib/chat/blocks/price-range';

/** Rows listed before "Show all"; a tapped dot's row is always shown. */
export const PRICE_RANGE_ROWS_SHOWN = 6;

export const PRICE_LIST_PATH = '/settings/money/price-list';

/**
 * "What do I usually charge?" — the price-list figure and every price the
 * owner has quoted for the same thing, as dots on one strip.
 *
 * Tapping a dot picks out its quote below (date, reference, the line as he
 * wrote it, the price). A row opens that quote; the go-row opens the price
 * list. Nothing here sends or changes anything, so nothing here is orange.
 *
 * 🚨 Every figure is off the block, which the server built from its own reads.
 * The footer says where it looked and never that there is nowhere left to
 * look (#31).
 */
export interface PriceRangeProps {
  block: PriceRangeBlock;
  live: boolean;
  /** Unused: nothing here answers Mucka in words. Kept so every component takes the same props. */
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

export function PriceRange({ block, live, onOpen }: PriceRangeProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  const scale = block.dots.length ? stripScale(block) : null;
  const range = drawnRange(block);
  const positions = new Map(dotPositions(block).map(p => [p.lineId, p]));

  const listed: PriceRangeDot[] = all ? block.dots : block.dots.slice(0, PRICE_RANGE_ROWS_SHOWN);
  const pickedDot = picked ? block.dots.find(d => d.lineId === picked) : undefined;
  if (pickedDot && !listed.includes(pickedDot)) listed.push(pickedDot);
  const hidden = block.dots.length - listed.length;

  const unit = block.listItem?.unit && block.listItem.unit !== 'each' ? `/${block.listItem.unit}` : '';
  const empty = block.dots.length === 0 && !block.listItem;

  return (
    <div className="mchat-price" role="group" aria-label={`Prices for ${block.query}`}>
      {scale && (
        <>
          <div className="mchat-price-strip">
            <span className="mchat-price-track" aria-hidden="true" />
            {range && range.high > range.low && (
              <span
                className="mchat-price-band"
                aria-hidden="true"
                style={{ left: `${stripX(range.low, scale)}%`, width: `${stripX(range.high, scale) - stripX(range.low, scale)}%` }}
              />
            )}
            {block.listItem && (
              <span className="mchat-price-list" style={{ left: `${stripX(block.listItem.price, scale)}%` }}>
                <span className="mchat-price-list-label">List {pounds(block.listItem.price)}{unit}</span>
              </span>
            )}
            {block.dots.map(d => {
              const at = positions.get(d.lineId);
              const on = picked === d.lineId;
              return (
                <button
                  key={d.lineId}
                  type="button"
                  className={`mchat-price-dot${on ? ' mchat-price-dot--on' : ''}`}
                  style={{ left: `${at?.x ?? 0}%`, ['--stack' as string]: String(Math.min(at?.stack ?? 0, 3)) }}
                  aria-pressed={on}
                  aria-label={`${d.dateLabel}, ${d.reference}, ${pounds(d.unitPrice)}`}
                  onClick={() => setPicked(on ? null : d.lineId)}
                >
                  <span className="mchat-price-pip" />
                </button>
              );
            })}
          </div>
          <div className="mchat-price-axis" aria-hidden="true">
            <span>{pounds(scale.min)}</span>
            <span>{pounds(scale.max)}</span>
          </div>
        </>
      )}

      {empty ? (
        <p className="mchat-price-note">{priceRangeEmptyText(block)}</p>
      ) : (
        <p className="mchat-price-note">
          {block.dots.length > 0
            ? `${capitalise(countPhrase(block))} in the last ${block.windowMonths} months`
              + (range ? (range.low === range.high ? `, all ${pounds(range.low)}` : `, ${pounds(range.low)} to ${pounds(range.high)}`) : '')
              + '.'
            : `None of your sent quotes from the last ${block.windowMonths} months had a line matching "${block.query}".`}
          {block.listItem && ` Your price list has ${block.listItem.name} at ${pounds(block.listItem.price)}${unit}.`}
          {block.otherListMatches > 0 && ` ${block.otherListMatches === 1 ? 'One more item' : `${block.otherListMatches} more items`} on the price list also matched.`}
        </p>
      )}

      {listed.length > 0 && (
        <div role="list" aria-label={`${block.dots.length} quoted prices`}>
          {listed.map(d => (
            <button
              key={d.lineId}
              type="button"
              role="listitem"
              className={`mchat-price-row${picked === d.lineId ? ' mchat-price-row--on' : ''}`}
              disabled={!live}
              onClick={() => onOpen(d.path)}
            >
              <span className="mchat-price-when">{d.dateLabel}</span>
              <span className="mchat-price-what">
                <span className="mchat-price-desc">{d.description}</span>
                <span className="mchat-price-ref">
                  {d.reference} · {PRICE_RANGE_STATUS_LABEL[d.status]}{d.quantity !== 1 ? ` · ${trimQty(d.quantity)} on the quote` : ''}
                </span>
              </span>
              <span className="mchat-price-amount">{pounds(d.unitPrice)}{d.quantity !== 1 ? ' each' : ''}</span>
            </button>
          ))}
          {hidden > 0 && (
            <button type="button" className="mchat-price-more" onClick={() => setAll(true)}>
              Show all {block.dots.length}
            </button>
          )}
        </div>
      )}

      <p className="mchat-price-footer">{priceRangeFooter(block)}</p>

      <button type="button" className="mchat-row mchat-price-go" disabled={!live} onClick={() => onOpen(PRICE_LIST_PATH)}>
        <span className="mchat-row-main"><span className="mchat-row-title">Open the price list</span></span>
        <Icon icon={ChevronRight} size={15} className="mchat-row-go" />
      </button>
    </div>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function trimQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}
